import React, { useState, useEffect } from 'react';
import { LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { Eye, MessageCircle, Star, Heart, Download, TrendingUp, Award } from 'lucide-react';
import { api } from '../services/api';
import { useAuth } from '../context/AuthContext';

export default function AnalyticsDashboard() {
  const { token } = useAuth();
  const [analytics, setAnalytics] = useState([]);
  const [selectedBusiness, setSelectedBusiness] = useState(null);
  const [historyData, setHistoryData] = useState([]);
  const [comparisonData, setComparisonData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [days, setDays] = useState(30);

  useEffect(() => {
    loadAnalytics();
  }, []);

  useEffect(() => {
    if (selectedBusiness) {
      loadHistory(selectedBusiness.business_id);
      if (selectedBusiness.categoria) {
        loadComparison(selectedBusiness.categoria);
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedBusiness, days]);

  const loadAnalytics = async () => {
    try {
      setLoading(true);
      const res = await api.get('/analytics/dashboard');
      setAnalytics(res.data);
      if (res.data.length > 0) {
        setSelectedBusiness(res.data[0]);
      }
    } catch (error) {
      console.error('Erro ao carregar analytics:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadHistory = async (businessId) => {
    try {
      const res = await api.get(`/analytics/history/${businessId}`, {
        params: { days }
      });
      setHistoryData(res.data);
    } catch (error) {
      console.error('Erro ao carregar histórico:', error);
    }
  };

  const loadComparison = async (categoria) => {
    try {
      const res = await api.get(`/analytics/compare/${categoria}`);
      setComparisonData(res.data);
    } catch (error) {
      console.error('Erro ao carregar comparação:', error);
    }
  };

  const handleExportPDF = async () => {
    try {
      const res = await api.get('/analytics/export-pdf', {
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `analytics_${Date.now()}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      console.error('Erro ao exportar PDF:', error);
      alert('Erro ao exportar relatório');
    }
  };

  if (loading) {
    return (
      <div className="text-center py-12">
        <div className="inline-block w-8 h-8 border-4 border-[var(--primary)] border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  if (analytics.length === 0) {
    return (
      <div className="bg-white rounded-2xl p-12 text-center">
        <div className="w-16 h-16 bg-[#F3E8FF] rounded-full flex items-center justify-center mx-auto mb-4">
          <TrendingUp className="w-8 h-8 text-[var(--primary)]" />
        </div>
        <h3 className="text-xl font-bold mb-2" style={{ fontFamily: 'Manrope' }}>
          Nenhum dado ainda
        </h3>
        <p className="text-[var(--text-secondary)]">
          Cadastre um negócio para ver as estatísticas
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header with Export Button */}
      <div className="flex items-center justify-between">
        <h2 className="text-xl font-bold" style={{ fontFamily: 'Manrope' }}>
          Analytics dos Negócios
        </h2>
        <button
          onClick={handleExportPDF}
          className="flex items-center gap-2 bg-[var(--primary)] text-white hover:bg-[#450066] rounded-full px-6 py-2.5 font-semibold transition-all shadow-sm hover:shadow-md active:scale-95"
          data-testid="export-pdf-button"
        >
          <Download className="w-4 h-4" />
          Exportar PDF
        </button>
      </div>

      {/* Business Selector */}
      {analytics.length > 1 && (
        <div className="bg-white rounded-xl border border-[var(--border)] p-4">
          <label className="block text-sm font-medium mb-2">Selecione um negócio:</label>
          <select
            value={selectedBusiness?.business_id || ''}
            onChange={(e) => {
              const business = analytics.find(b => b.business_id === e.target.value);
              setSelectedBusiness(business);
            }}
            className="w-full px-4 py-2 rounded-lg border border-[var(--border)] focus:ring-2 focus:ring-[var(--primary)]/20 focus:border-[var(--primary)]"
          >
            {analytics.map(item => (
              <option key={item.business_id} value={item.business_id}>
                {item.business_name}
              </option>
            ))}
          </select>
        </div>
      )}

      {selectedBusiness && (
        <>
          {/* Badges */}
          {selectedBusiness.badges && selectedBusiness.badges.length > 0 && (
            <div className="bg-gradient-to-r from-purple-50 to-pink-50 rounded-xl border border-purple-200 p-6">
              <div className="flex items-center gap-2 mb-3">
                <Award className="w-5 h-5 text-[var(--primary)]" />
                <h3 className="font-bold text-lg">Conquistas Desbloqueadas</h3>
              </div>
              <div className="flex flex-wrap gap-2">
                {selectedBusiness.badges.map((badge, index) => (
                  <span
                    key={index}
                    className="inline-flex items-center px-4 py-2 rounded-full text-sm font-medium bg-white border-2 border-[var(--primary)] text-[var(--primary)] shadow-sm"
                  >
                    {badge}
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* Metrics Cards */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="bg-white rounded-xl border border-[var(--border)] p-4">
              <div className="flex items-center gap-2 mb-2">
                <Eye className="w-5 h-5 text-blue-600" />
                <span className="text-sm text-blue-600 font-medium">Visualizações</span>
              </div>
              <div className="text-3xl font-bold text-blue-900">{selectedBusiness.views_count}</div>
            </div>

            <div className="bg-white rounded-xl border border-[var(--border)] p-4">
              <div className="flex items-center gap-2 mb-2">
                <MessageCircle className="w-5 h-5 text-green-600" />
                <span className="text-sm text-green-600 font-medium">WhatsApp</span>
              </div>
              <div className="text-3xl font-bold text-green-900">{selectedBusiness.whatsapp_clicks}</div>
            </div>

            <div className="bg-white rounded-xl border border-[var(--border)] p-4">
              <div className="flex items-center gap-2 mb-2">
                <Star className="w-5 h-5 text-yellow-600" />
                <span className="text-sm text-yellow-600 font-medium">Avaliação</span>
              </div>
              <div className="text-3xl font-bold text-yellow-900">
                {selectedBusiness.rating_avg > 0 ? selectedBusiness.rating_avg : '-'}
              </div>
              <div className="text-xs text-yellow-700 mt-1">{selectedBusiness.rating_count} avaliações</div>
            </div>

            <div className="bg-white rounded-xl border border-[var(--border)] p-4">
              <div className="flex items-center gap-2 mb-2">
                <Heart className="w-5 h-5 text-red-600" />
                <span className="text-sm text-red-600 font-medium">Favoritos</span>
              </div>
              <div className="text-3xl font-bold text-red-900">{selectedBusiness.favorites_count}</div>
            </div>
          </div>

          {/* Time Period Selector */}
          <div className="bg-white rounded-xl border border-[var(--border)] p-4">
            <label className="block text-sm font-medium mb-2">Período:</label>
            <div className="flex gap-2">
              {[7, 30, 90].map(d => (
                <button
                  key={d}
                  onClick={() => setDays(d)}
                  className={`px-4 py-2 rounded-lg font-medium transition-all ${
                    days === d
                      ? 'bg-[var(--primary)] text-white'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  {d} dias
                </button>
              ))}
            </div>
          </div>

          {/* History Chart */}
          {historyData.length > 0 && (
            <div className="bg-white rounded-xl border border-[var(--border)] p-6">
              <h3 className="font-bold text-lg mb-4">Histórico de Visualizações e Cliques</h3>
              <ResponsiveContainer width="100%" height={300}>
                <LineChart data={historyData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis 
                    dataKey="date" 
                    tick={{ fontSize: 12 }}
                    tickFormatter={(value) => new Date(value).toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' })}
                  />
                  <YAxis />
                  <Tooltip 
                    labelFormatter={(value) => new Date(value).toLocaleDateString('pt-BR')}
                  />
                  <Legend />
                  <Line type="monotone" dataKey="views" stroke="#3B82F6" name="Visualizações" strokeWidth={2} />
                  <Line type="monotone" dataKey="whatsapp_clicks" stroke="#10B981" name="Cliques WhatsApp" strokeWidth={2} />
                </LineChart>
              </ResponsiveContainer>
            </div>
          )}

          {/* Comparison Chart */}
          {comparisonData && (
            <div className="bg-white rounded-xl border border-[var(--border)] p-6">
              <h3 className="font-bold text-lg mb-2">Comparação com Categoria</h3>
              <p className="text-sm text-[var(--text-secondary)] mb-4">
                {comparisonData.total_businesses} negócios na categoria
              </p>
              
              {comparisonData.comparison.map((item, index) => (
                <div key={index} className="mb-6">
                  <h4 className="font-semibold mb-3">{item.business_name}</h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-sm">Visualizações</span>
                        <span className={`text-sm font-bold ${item.views_vs_avg >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                          {item.views_vs_avg >= 0 ? '+' : ''}{item.views_vs_avg}%
                        </span>
                      </div>
                      <div className="flex items-center gap-2">
                        <div className="flex-1 bg-gray-200 rounded-full h-2">
                          <div 
                            className="bg-blue-500 h-2 rounded-full"
                            style={{ width: `${Math.min((item.views / (item.category_avg_views * 2)) * 100, 100)}%` }}
                          />
                        </div>
                        <span className="text-sm font-medium">{item.views}</span>
                      </div>
                      <p className="text-xs text-gray-500 mt-1">Média da categoria: {item.category_avg_views}</p>
                    </div>

                    <div>
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-sm">Cliques WhatsApp</span>
                        <span className={`text-sm font-bold ${item.clicks_vs_avg >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                          {item.clicks_vs_avg >= 0 ? '+' : ''}{item.clicks_vs_avg}%
                        </span>
                      </div>
                      <div className="flex items-center gap-2">
                        <div className="flex-1 bg-gray-200 rounded-full h-2">
                          <div 
                            className="bg-green-500 h-2 rounded-full"
                            style={{ width: `${Math.min((item.whatsapp_clicks / (item.category_avg_clicks * 2)) * 100, 100)}%` }}
                          />
                        </div>
                        <span className="text-sm font-medium">{item.whatsapp_clicks}</span>
                      </div>
                      <p className="text-xs text-gray-500 mt-1">Média da categoria: {item.category_avg_clicks}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
}
